import { useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useListingBySlug } from "@/hooks/use-listings";
import Layout from "@/components/Layout";
import PageMeta from "@/components/seo/PageMeta";
import {
  buildLocalBusinessSchema,
  buildBreadcrumbSchema,
  buildFAQSchema,
} from "@/components/seo/schemas";
import SpaProfileHero from "@/components/spa/SpaProfileHero";
import SpaProfileDetails from "@/components/spa/SpaProfileDetails";
import SpaContentSections, { buildSpaFaqs } from "@/components/spa/SpaContentSections";
import ClaimListing from "@/components/spa/ClaimListing";
import { lazy, Suspense } from "react";
const SpaProfileMap = lazy(() => import("@/components/spa/SpaProfileMap"));
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { optimizeImageUrl } from "@/lib/image-utils";

function GalleryCarousel({ images, name }: { images: string[]; name: string }) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const scrollAmount = container.clientWidth * 0.5;
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  return (
    <div className="p-5 rounded-2xl border border-border bg-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-lg text-foreground">Gallery</h2>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => scroll("left")}
            className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => scroll("right")}
            className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory"
      >
        {images.map((img, i) => (
          <div key={i} className="shrink-0 w-[calc(50%-6px)] snap-start rounded-xl overflow-hidden border border-border">
            <img
              src={img}
              alt={`${name} gallery ${i + 1}`}
              loading="lazy"
              className="w-full h-auto object-cover aspect-[16/10]"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SpaProfile() {
  const { slug } = useParams<{ slug: string }>();
  const { data: listing, isLoading } = useListingBySlug(slug);

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 lg:px-8 py-24 flex justify-center">
          <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!listing) {
    return (
      <Layout>
        {/* noIndex: an unknown spa slug should never appear in search */}
        <PageMeta
          title="Spa Not Found"
          description="The spa you're looking for couldn't be found. Browse our directory to find top-rated medical spas near you."
          path={`/spa/${slug ?? ""}`}
          noIndex
        />
        <div className="container mx-auto px-4 lg:px-8 py-24 text-center">
          <h1 className="font-display text-3xl font-bold text-foreground mb-3">Spa Not Found</h1>
          <p className="text-muted-foreground mb-8">We couldn't find the spa you're looking for.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </Layout>
    );
  }

  const locationLabel = [listing.city, listing.state].filter(Boolean).join(", ");
  const spaDescription = listing.description
    ? listing.description.slice(0, 160)
    : `${listing.name} is a medical spa${
        locationLabel ? ` in ${locationLabel}` : ""
      }${
        listing.tags && listing.tags.length
          ? ` offering ${listing.tags.slice(0, 4).join(", ")}`
          : ""
      }. View services, hours, contact info, and reviews.`;

  const pageTitle = `${listing.name}${
    locationLabel ? ` — ${locationLabel}` : ""
  } | MedSpa Reviews, Treatments & Hours`;

  const spaFaqs = buildSpaFaqs(listing);

  return (
    <Layout>
      <PageMeta
        title={pageTitle}
        description={spaDescription}
        path={`/spa/${listing.slug}`}
        ogImage={listing.image_url || listing.logo_url || undefined}
        ogType="business.business"
        jsonLd={[
          buildLocalBusinessSchema(listing),
          buildBreadcrumbSchema([
            { name: "Home", url: "https://medspadirectorypro.com/" },
            { name: "Browse", url: "https://medspadirectorypro.com/browse" },
            { name: listing.name, url: `https://medspadirectorypro.com/spa/${listing.slug}` },
          ]),
          ...(spaFaqs.length > 0 ? [buildFAQSchema(spaFaqs)] : []),
        ]}
      />
      <SpaProfileHero listing={listing} />
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10 lg:py-14">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6 order-2 lg:order-1">
            {listing.image_url && (
              <div className="rounded-2xl border border-border overflow-hidden">
                <img
                  src={listing.image_url}
                  alt={`${listing.name} MedSpa in ${[listing.city, listing.state].filter(Boolean).join(", ")}`}
                  loading="lazy"
                  className="w-full h-auto object-cover max-h-[400px]"
                />
              </div>
            )}
            <Suspense fallback={<div className="h-[170px] rounded-2xl border border-border bg-card" />}>
              <SpaProfileMap listing={listing} />
            </Suspense>

            {/* Owner-provided "About" — only shown when present, since the SEO content
                sections below will always render even if no description was supplied */}
            {listing.description && (
              <div className="p-5 rounded-2xl border border-border bg-card">
                <h2 className="font-semibold text-lg text-foreground mb-3">
                  In their own words
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {listing.description}
                </p>
              </div>
            )}

            {/* Unique SEO content sections — see SpaContentSections.tsx for details */}
            <SpaContentSections listing={listing} />

            <ClaimListing listing={listing} />
            {listing.gallery && listing.gallery.length > 0 && (
              <GalleryCarousel images={listing.gallery} name={listing.name} />
            )}
          </div>
          <div className="space-y-6 order-1 lg:order-2">
            {listing.logo_url && (
              <div className="rounded-2xl border border-border bg-white overflow-hidden p-4 flex items-center justify-center">
                <img
                  src={optimizeImageUrl(listing.logo_url, 400)}
                  alt={`${listing.name} MedSpa logo`}
                  loading="lazy"
                  className="w-full max-h-48 object-contain"
                />
              </div>
            )}
            <SpaProfileDetails listing={listing} />
          </div>
        </div>
      </div>
    </Layout>
  );
}
